<script lang="ts" generics="T extends TaskProgressType | GoalProgressType">
  import { appStore } from "$app/store.svelte"
  import { onMount } from "svelte"
  import { Pulse } from "svelte-loading-spinners"
  import { xhr, toast } from "$app/utils"
  import Honeybadger from "$app/utils/honeybadger"
  import { X } from "lucide-svelte"

  const plan = appStore.plan
  interface Props {
    goal: GoalType | TaskType
    type?: string
    oncreate: (p: ProgressType) => void
    onclose: () => void
  }

  let { goal, type = "goal", oncreate, onclose }: Props = $props()

  let isLoading = $state(false)
  let progresses = $state([])
  let avatarUrl = $state(null)
  let userName = $state("")

  let url = $derived(type === "goal" ? "goal_progress" : "task_progress")

  onMount(async () => {
    isLoading = true
    getProgresses()
  })

  let currentWeekInQuarter = 0
  let weekDateRangesForQuarter = []

  async function getProgresses(): Promise<void> {
    isLoading = true

    try {
      const resp = await xhr.get(`/plans/${plan.id}/${url}?id=${goal.id}`)
      isLoading = false
      progresses = resp.data.progresses
      avatarUrl = resp.data.avatar_thumb_url
      userName = resp.data.user_name
      currentWeekInQuarter = resp.data.current_week_in_quarter
      weekDateRangesForQuarter = resp.data.week_date_ranges_for_quarter
    } catch (e: any) {
      isLoading = false
      if (typeof Honeybadger.notify !== "undefined") {
        Honeybadger.notify(e)
      }
      toast("There was an error loading the progresses", "error")
    } finally {
      progresses = progresses
      avatarUrl = avatarUrl
      userName = userName
    }
  }

  async function xhrProgress(data: T) {
    let progress_data = {}
    if (type === "task") {
      progress_data = { task_progress: data }
    } else {
      progress_data = { goal_progress: data }
    }
    return xhr.post(`/plans/${plan.id}/${url}`, progress_data)
  }

  // Create Goal Progress
  let newProgressText = $state("")
  let savingProgress = $state(false)
  async function createProgress() {
    if (newProgressText.length === 0) {
      return
    }
    savingProgress = true

    let progress_data = $state({} as T)
    if (type === "task") {
      progress_data = {
        task_id: goal.id,
        progress_text: newProgressText,
        created_at: new Date().toUTCString()
      } as T
    } else {
      progress_data = {
        goal_id: goal.id,
        progress_text: newProgressText,
        created_at: new Date().toUTCString()
      } as T
    }

    try {
      const resp = await xhrProgress(progress_data)
      savingProgress = false
      progresses = [resp.data, ...progresses]
      oncreate(resp.data)
    } catch (e) {
      if (typeof Honeybadger.notify !== "undefined") {
        Honeybadger.notify(e)
      }
    } finally {
      newProgressText = ""
    }
  }
</script>

<!-- svelte-ignore a11y_click_events_have_key_events -->
<div
  class="border border-gray-200 rounded shadow-lg"
  data-name="progress-container"
  role="button"
  tabindex="0"
  ondblclick={(e) => {
    e.stopPropagation()
    e.preventDefault()
  }}
>
  <div class="block absolute top-0 right-0 pt-2 pr-2">
    <button
      type="button"
      onclick={() => onclose()}
      data-name="close-modal"
      class="bg-core rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
    >
      <span class="sr-only">Close</span>
      <X class="size-5" strokeWidth="3" />
    </button>
  </div>
  {#if isLoading}
    <div class="py-12 flex justify-center w-full">
      <Pulse size="240" color="#cdcdcd" unit="px" duration="1s" />
    </div>
  {:else}
    <div class="border-b border-gray-400 py-2 w-full">
      <p class="text-lg mb-2 px-4">
        Weekly Progress for <span class="font-bold">{goal.name}</span>
      </p>
      <div class="border-gray-200 border-t pt-2 px-4">
        <div class="flex-1 flex items-start justify-between space-x-4">
          <img
            class="mt-2 w-10 h-10 bg-gray-300 rounded-full flex-shrink-0"
            src={avatarUrl}
            alt={userName}
          />
          <div class="flex flex-grow flex-wrap">
            <textarea
              bind:value={newProgressText}
              rows="2"
              class="flex-1 max-w-lg shadow-sm block w-full !mt-0 border !rounded-none !rounded-l-md sm:text-sm border-gray-300"
            ></textarea>
            <button
              class="bg-green-400 text-sm text-white py-2 px-2 w-full md:w-20 rounded-none rounded-r-md shadow-sm border-gray-300"
              data-name="save-progress"
              onclick={() => {
                if (!savingProgress) {
                  createProgress()
                }
              }}
            >
              {#if savingProgress}
                Saving...
              {:else}
                Add Progress
              {/if}
            </button>
          </div>
        </div>
      </div>
    </div>
    <ul
      class="grid grid-cols-1 max-h-48 overflow-y-auto"
      data-name="progresses-list"
    >
      {#each progresses as progress}
        <li
          class="col-span-1 bg-white border-b border-gray-200"
          data-name="progress-item"
        >
          <div
            class="w-full flex items-start justify-between px-6 py-2 space-x-6"
          >
            <img
              class="w-10 h-10 bg-gray-300 rounded-full flex-shrink-0"
              src={progress.user_avatar_url}
              alt={progress.whodunnit}
            />
            <div class="flex-1">
              <div class="flex items-center space-x-3">
                <h3 class="text-gray-900 font-bold">
                  {progress.whodunnit}
                </h3>
                <span class="flex-shrink-0 ml-4 inline-block text-xs"
                  >{progress.when}</span
                >
              </div>
              <p class="mt-1 text-gray-500 text-sm" data-name="progress-text">
                {progress.progress_text}
              </p>
            </div>
          </div>
        </li>
      {:else}
        <li class="sm:flex sm:space-x-5 p-6">
          <p class="font-lg font-bold mb-3 text-center text-gray-400">
            {#if type === "task"}
              No progress entered for this task
            {:else}
              No progress entered for this goal
            {/if}
          </p>
        </li>
      {/each}
    </ul>
  {/if}
</div>

<style>
  textarea:focus {
    border-color: var(--color-green-500);
    outline-color: var(--color-green-500);
    border-radius: 0.3rem 0 0 0.3rem;
  }
</style>
